/**
 * Pure protagonist resolution — shared between the in-IDE Preview player
 * (ls-preview) and the F Studio monitor stage (ls-studio).
 *
 * The protagonist (MC) is the single character the player/monitor places on the
 * LEFT; everyone else is forced RIGHT. This file owns the ONE source-of-truth
 * parse used by both: a project's `CLAUDE.md` carries a `- Protagonist: <Name>`
 * line (written by `createProjectSkeleton`), and we slugify that name to the
 * SAME id form the compiler emits for `char_show` character ids
 * (lowercase, non-alphanumeric runs → `_`, trimmed).
 *
 * This module is webview-safe — it has ZERO `node:*` imports, so it can be
 * pulled into the ls-studio browser bundle. The file-path variant that reads
 * CLAUDE.md off disk lives in `./node/protagonist.ts` (host-only).
 *
 * History: these two functions were originally defined in
 * `packages/ls-preview/src/episode-publish-core.ts`. They were lifted here so
 * the shared stage compute can bucket characters left/right exactly like the
 * player. ls-preview re-exports them from their old location, unchanged.
 */
/**
 * Slugify a display name to the compiler's character-id form. Mirror of
 * `createProjectSkeleton`'s `sanitizeSlug` so the resolved protagonist matches
 * the compiled `char_show` character id (and the slugified F Studio card
 * `character` token) exactly. Returns null when nothing survives.
 */
export function sanitizeCharacterSlug(value) {
    const slug = value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    return slug.length > 0 ? slug : null;
}
/**
 * Parse the `- Protagonist: <Name>` line from a CLAUDE.md body and slugify it
 * to the compiler's character-id form. Returns null when the line is absent or
 * empty. Pure — no I/O. Exported for unit testing and re-used by both webviews.
 */
export function protagonistFromClaudeMd(claudeMd) {
    const match = claudeMd.match(/^[-*]?\s*Protagonist:\s*(.+?)\s*$/im);
    const captured = match?.[1];
    if (!captured) {
        return null;
    }
    return sanitizeCharacterSlug(captured);
}
